import { Component, OnInit } from '@angular/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { HttpRequestService } from 'src/app/core/services';
import { ActivatedRoute, Router } from '@angular/router';
import { NzModalService } from 'ng-zorro-antd/modal';
import { toNumber } from 'ng-zorro-antd/core/util';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { ReportContentDetailsComponent } from '../report-content-details/report-content-details.component';
@Component({
  selector: 'app-report-content-list',
  templateUrl: './report-content-list.component.html',
  styleUrls: ['./report-content-list.component.scss'],
})
export class ReportContentListComponent implements OnInit {
  // ── Tabs ───────────────────────────────────────────────
  selectedTabIndex = 0;

  // ── Table data ─────────────────────────────────────────
  allReports: any[] = [];
  totalDataCount = 0;
  pageSize = 12;
  pageIndex = 1;
  loading = false;
  skipIndex = 0;

  // ── Filters ────────────────────────────────────────────
  dateRange: any;
  startDate: any;
  endDate: any;
  reasonSearch = '';
  searchReport: Subject<string> = new Subject<string>();

  // ── Modal state ────────────────────────────────────────
  adminNoteModalVisible = false;
  adminNoteText = '';
  pendingAction: { id: string; status: 'resolved' | 'rejected' } | null = null;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private httpRequestService: HttpRequestService,
    private notificationService: NzNotificationService,
    private drawerService: NzDrawerService,
  ) {
    this.selectedTabIndex = toNumber(
      this.activatedRoute.snapshot.queryParams['tabIndex'] ?? 0,
    );
  }

  ngOnInit(): void {
    this.searchReport.pipe(debounceTime(600)).subscribe((value) => {
      this.reasonSearch = value;
      this.resetPagination();
      this.getAllReports();
    });
    this.getAllReports();
  }

  // ── Tab ────────────────────────────────────────────────
  onTabChange(index: number): void {
    this.selectedTabIndex = index;
    this.router.navigate([], {
      queryParams: { tabIndex: index },
      queryParamsHandling: 'merge',
    });
    this.resetPagination();
    this.getAllReports();
  }

  // ── Filters ────────────────────────────────────────────
  onChangeDateRange(date: any): void {
    if (date && date.length === 2) {
      this.startDate = date[0];
      this.endDate = date[1];
    } else {
      this.startDate = null;
      this.endDate = null;
    }
    this.resetPagination();
    this.getAllReports();
  }

  onReasonSearch(value: string): void {
    this.searchReport.next(value);
  }

  // ── Pagination ─────────────────────────────────────────
  onQueryParamsChange(params: NzTableQueryParams): void {
    const { pageSize, pageIndex } = params;
    this.pageSize = pageSize;
    this.pageIndex = pageIndex;
    this.skipIndex = pageSize * (pageIndex - 1);
    this.getAllReports();
  }

  resetPagination(): void {
    this.pageIndex = 1;
    this.skipIndex = 0;
  }

  // ── API ────────────────────────────────────────────────
  getAllReports(): void {
    const params: any = { skip: this.skipIndex, limit: this.pageSize };

    if (this.selectedTabIndex === 0) {
      params.status = 'pending';
    } else {
      params.status = ['resolved', 'rejected'];
    }

    if (this.reasonSearch) params.reason = this.reasonSearch;

    if (this.startDate && this.endDate) {
      params.startDate = (
        this.startDate instanceof Date
          ? this.startDate.toISOString()
          : this.startDate
      ).split('T')[0];
      params.endDate = (
        this.endDate instanceof Date ? this.endDate.toISOString() : this.endDate
      ).split('T')[0];
    }

    this.loading = true;
    this.httpRequestService.request('get', 'report-contents', params).subscribe(
      (res) => {
        this.loading = false;
        this.allReports = res.data;
        this.totalDataCount = res.totalCount;
      },
      () => {
        this.loading = false;
      },
    );
  }

  // ── Action modal ───────────────────────────────────────
  openActionModal(id: string, status: 'resolved' | 'rejected'): void {
    this.pendingAction = { id, status };
    this.adminNoteText = '';
    this.adminNoteModalVisible = true;
  }

  confirmAction(): void {
    if (!this.pendingAction) return;
    const { id, status } = this.pendingAction;
    this.httpRequestService
      .request('put', `report-contents/${id}`, {
        status,
        adminNote: this.adminNoteText,
      })
      .subscribe(() => {
        this.notificationService.success(
          '',
          status === 'resolved'
            ? 'Report Resolved Successfully'
            : 'Report Rejected Successfully',
        );
        this.adminNoteModalVisible = false;
        this.pendingAction = null;
        this.getAllReports();
      });
  }

  cancelAction(): void {
    this.adminNoteModalVisible = false;
    this.pendingAction = null;
  }

  // ── Helpers ────────────────────────────────────────────
  getContentName(data: any): string {
    if (data.question && typeof data.question === 'object')
      return data.question.questionText || data.question.title || '—';
    if (data.editorial && typeof data.editorial === 'object')
      return data.editorial.title || '—';
    if (data.currentAffair && typeof data.currentAffair === 'object')
      return data.currentAffair.title || '—';
    return '—';
  }

  navigateToUpdate(data: any): void {
    if (data.question) {
      const id =
        typeof data.question === 'object' ? data.question._id : data.question;
      const route = data.question?.isParagraph
        ? '/main/question/update-paragraph-question'
        : '/main/question/update';
      this.router.navigate([route, id], {
        queryParams: { fromReport: true },
      });
    } else if (data.editorial) {
      const id =
        typeof data.editorial === 'object'
          ? data.editorial._id
          : data.editorial;
      this.router.navigate(['/main/editorials/update', id], {
        queryParams: { fromReport: true },
      });
    } else if (data.currentAffair) {
      const id =
        typeof data.currentAffair === 'object'
          ? data.currentAffair._id
          : data.currentAffair;
      this.router.navigate(['/main/current-affairs/update', id], {
        queryParams: { fromReport: true },
      });
    }
  }

  navigateToDetails(id: string): void {
    this.drawerService.create({
      nzTitle: 'Reported Content Details',
      nzWidth: '50%',
      nzContent: ReportContentDetailsComponent,
      nzContentParams: {
        id: id
      }
    });
  }

  canNavigate(data: any): boolean {
    return (
      (data.question && typeof data.question === 'object') ||
      (data.editorial && typeof data.editorial === 'object') ||
      (data.currentAffair && typeof data.currentAffair === 'object')
    );
  }

  getContentType(data: any): string {
    if (data.question) return 'Question';
    if (data.editorial) return 'Editorial';
    if (data.currentAffair) return 'Current Affair';
    return '—';
  }

  getContentTypeColor(data: any): string {
    if (data.question) return 'blue';
    if (data.editorial) return 'purple';
    if (data.currentAffair) return 'orange';
    return 'default';
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'pending':
        return 'gold';
      case 'resolved':
        return 'green';
      case 'rejected':
        return 'red';
      default:
        return 'default';
    }
  }
}
